import { bleedFoldMarginsFor, checkBleedOverlap } from '@messenger/utils'
import type {
  BleedFoldMargin,
  BleedMargins,
  BleedPadding,
  FoldLayoutSource,
  GrowthCeilingKept,
} from '@messenger/utils'
import { layoutMarginsOf } from '@/utils/editor/layoutMargins'
import { EventCategory, logger } from '@/utils/logger'
import { loggerSentry } from '@/utils/loggerSentry'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import {
  EDITOR_TEXT_BOX_ITEM_TYPE,
  type EditorInkRect,
} from '@/utils/editor-text-box/editorTextBoxAdapter'

/**
 * What a box on the preview needs to judge its own ink against the page:
 * the layout's margins, the page's size and padding, and the ids that name
 * the page in a log line — a forty-page product has to point at the page.
 */
export type EditorTextBoxBleedReport = {
  bleeds: BleedMargins | null
  pageWidth: number
  pageHeight: number
  padding: BleedPadding | null
  /** A trifold page's fold margins (`bleedFoldMarginsFor`), which replace
   * the center margins; undefined for every other product. */
  foldMargins?: BleedFoldMargin[]
  pageNumber: number | null
  orderPageId: string | null
  orderId: string | null
  orderPageSetId: string | null
  pageSetId: string | null
}

export type PreviewBleedReportSource = {
  layout:
    | (NonNullable<Parameters<typeof layoutMarginsOf>[0]> & FoldLayoutSource)
    | null
    | undefined
  pageWidth: number
  pageHeight: number
  padding: BleedPadding | null
  /** The page JSON's `pageProperties.pageNumber`. */
  storedPageNumber: number | null | undefined
  /** The page's 1-based position among the preview's pages. */
  pagePosition: number | null | undefined
  orderPageId: string | null
  orderId: string | null
  orderPageSetId: string | null
  pageSetId: string | null
}

/**
 * The bleed report a preview page hands its boxes; none without a layout. A
 * trifold page's folds are placed by its JSON page number, as the canvas and
 * the proof place them; a page stored without one is placed by its position
 * in the preview, so it is still judged at its folds.
 */
export const previewBleedReportOf = ({
  layout,
  pageWidth,
  pageHeight,
  padding,
  storedPageNumber,
  pagePosition,
  orderPageId,
  orderId,
  orderPageSetId,
  pageSetId,
}: PreviewBleedReportSource): EditorTextBoxBleedReport | null => {
  if (!layout) return null
  const pageNumber = storedPageNumber ?? pagePosition ?? null
  return {
    bleeds: layoutMarginsOf(layout),
    pageWidth,
    pageHeight,
    padding,
    foldMargins: bleedFoldMarginsFor(layout, pageNumber ?? 0, pageWidth),
    pageNumber,
    orderPageId,
    orderId,
    orderPageSetId,
    pageSetId,
  }
}

export const PREVIEW_BLEED_WARNING_EVENT = 'preview_text_bleeding'

const reported = new Set<string>()

/**
 * Judges a settled box's ink on the preview and reports a crossing once per
 * page set, box and margin, to Datadog and to Sentry. The preview has no
 * badge to show; the log is how a bleeding page on a proof review is found.
 */
export const reportPreviewBleedWarning = (
  boxId: string,
  ink: EditorInkRect | null,
  frame: { x: number; y: number },
  report: EditorTextBoxBleedReport,
): void => {
  if (!ink || !report.bleeds) return
  const result = checkBleedOverlap(
    frame.x + ink.left,
    frame.y + ink.top,
    Math.max(0, ink.right - ink.left),
    Math.max(0, ink.bottom - ink.top),
    report.bleeds,
    report.pageWidth,
    report.pageHeight,
    report.padding,
    report.foldMargins,
  )
  if (!result.isOverlapping || !result.location) return
  const key = `${report.orderPageSetId}:${boxId}:${result.location}`
  if (reported.has(key)) return
  reported.add(key)
  countEditorProbeRender(`previewBleed:${boxId}:${result.location}`)
  const data = {
    orderId: report.orderId,
    orderPageSetId: report.orderPageSetId,
    pageSetId: report.pageSetId,
    orderPageId: report.orderPageId,
    pageNumber: report.pageNumber,
    itemId: boxId,
    itemType: EDITOR_TEXT_BOX_ITEM_TYPE,
    bleedType: result.bleedType,
    location: result.location,
  }
  logger.warn({
    message: PREVIEW_BLEED_WARNING_EVENT,
    category: EventCategory.DISCREPANCY,
    data,
  })
  loggerSentry.warn({
    message: PREVIEW_BLEED_WARNING_EVENT,
    category: EventCategory.DISCREPANCY,
    data,
  })
}

export const PREVIEW_LOAD_WARNING_EVENT = 'preview_text_fitted'
export const PREVIEW_KEPT_WARNING_EVENT = 'preview_text_preexisting'

const reportedFits = new Set<string>()

export type PreviewLoadResult = {
  fitted: boolean
  boundedBy: string | null
  /** Set when the stored frame is past its room (`boundedBy` `preexisting`): the room, what bounds it, one line of the text. */
  kept?: GrowthCeilingKept | null
}

/**
 * Logs, once per page set and box, a box whose load result made its text
 * smaller on the preview, or whose stored frame is past the room the page
 * leaves under it, to Datadog at info and never to Sentry: the load changed
 * no stored frame, so the line is a record of what the proof shows, not a
 * warning anyone acts on. `boundedBy` names what bounds the room under the
 * box (an item id, `margin`, `fold`, `preexisting`, or null for the page
 * bottom alone), as the worker's own geometry warnings do; a flagged frame's
 * line carries the room, what bounds it and one line of the box's text.
 */
export const reportPreviewLoadWarning = (
  boxId: string,
  kind: 'tinymce' | 'lotus',
  loadResult: PreviewLoadResult | null,
  report: EditorTextBoxBleedReport,
): void => {
  if (loadResult === null) return
  const kept = loadResult.kept ?? null
  if (!loadResult.fitted && kept === null) return
  const key = `${report.orderPageSetId}:${boxId}`
  if (reportedFits.has(key)) return
  reportedFits.add(key)
  countEditorProbeRender(`previewFitted:${boxId}:${loadResult.boundedBy}`)
  const message =
    kept !== null ? PREVIEW_KEPT_WARNING_EVENT : PREVIEW_LOAD_WARNING_EVENT
  const data = {
    orderId: report.orderId,
    orderPageSetId: report.orderPageSetId,
    pageSetId: report.pageSetId,
    orderPageId: report.orderPageId,
    pageNumber: report.pageNumber,
    itemId: boxId,
    kind,
    boundedBy: loadResult.boundedBy,
    ...(kept !== null
      ? {
          keptCeiling: kept.ceiling,
          keptBy: kept.boundedBy,
          lineHeight: kept.lineHeight,
        }
      : {}),
  }
  logger.info({
    message,
    category: EventCategory.DISCREPANCY,
    data,
  })
}
